import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '@/lib/utils'

/**
 * Figma (node 17382:13106 — Slider):
 * Track (outer): bg-muted (#f5f5f5), rounded-full, h-1 (4px), w-full
 * Range (filled): bg-primary (#171717), h-full, rounded-full
 * Thumb (node 17428:100438 — Slider / Item):
 *   Default: bg-white, border border-ring (#a3a3a3), size-3 (12px), rounded-full
 *   Hover/Focus: + shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]
 * Disabled: opacity-50, pointer-events-none
 * Orientation: horizontal (default), vertical
 */

export interface IntuiSliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  /** The id of the visible label that names each thumb (the focusable `slider`). */
  thumbLabelledBy?: string
}

const IntuiSlider = React.forwardRef<
  React.ComponentRef<typeof SliderPrimitive.Root>,
  IntuiSliderProps
>(({ className, defaultValue, value, thumbLabelledBy, ...props }, ref) => {
  const thumbCount =
    (value !== undefined ? value : defaultValue !== undefined ? defaultValue : [0]).length

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none',
        props.orientation === 'vertical' ? 'h-full flex-col items-center' : 'items-center',
        props.disabled && 'opacity-50 pointer-events-none',
        className
      )}
      defaultValue={defaultValue}
      value={value}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative grow overflow-hidden rounded-full bg-muted',
          props.orientation === 'vertical' ? 'w-1 h-full' : 'h-1 w-full'
        )}
      >
        <SliderPrimitive.Range
          className={cn(
            'absolute rounded-full bg-primary',
            props.orientation === 'vertical' ? 'w-full' : 'h-full'
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: thumbCount }).map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          aria-labelledby={thumbLabelledBy}
          className={cn(
            'block size-3 cursor-pointer rounded-full border border-ring bg-background',
            'focus-visible:outline-none focus-visible:shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]',
            'hover:shadow-[0px_0px_0px_3px_rgba(163,163,163,0.5)]',
            'transition-shadow'
          )}
        />
      ))}
    </SliderPrimitive.Root>
  )
})
IntuiSlider.displayName = 'IntuiSlider'

export { IntuiSlider }
